import React, { useState, useEffect } from 'react';
import { 
  ThemeProvider, 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Box, 
  Paper, 
  CircularProgress, 
  Alert,
  Stack
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import './App.css';
import FileSelector from './components/FileSelector';
import PipelineView from './components/PipelineView';
import ViewToggle from './components/ViewToggle';
import Legend from './components/Legend';
import WorkflowItems from './components/WorkflowItems';
import { YamlService } from './services/YamlService';
import { WorkflowTemplate, WorkflowPhase } from './types/WorkflowTypes';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetailedSteps, setShowDetailedSteps] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!selectedFile) {
      setWorkflow(null);
      return;
    }

    const loadYamlData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Reset workflow state when switching files
        setWorkflow(null);
        
        const data = await YamlService.loadYamlFile(selectedFile);
        
        // Skip if the file is a workflow items file
        if (selectedFile === '_workflow_items.yaml') {
          setLoading(false);
          return;
        }
        
        // Validate the data structure for workflow template
        if (!data || !data.phases || !Array.isArray(data.phases)) {
          console.error('Invalid workflow data structure:', data);
          setError('The selected file does not contain a valid workflow template');
          setLoading(false);
          return;
        }
        
        // Only set workflow data for scenario files
        setWorkflow(data);
        
        // Initialize expanded phases based on the loaded workflow
        if (data && data.phases) {
          const initialExpandedState: Record<string, boolean> = {};
          data.phases.forEach((phase: WorkflowPhase) => {
            // Start with all phases expanded for better visibility
            initialExpandedState[phase.name] = true;
          });
          setExpandedPhases(initialExpandedState);
        }
      } catch (err) {
        console.error('Error loading YAML data:', err);
        setError('Failed to load workflow data');
        setWorkflow(null);
      } finally {
        setLoading(false);
      }
    };

    loadYamlData();
  }, [selectedFile]);

  const handleFileSelect = (filename: string) => {
    setSelectedFile(filename);
  };

  const togglePhase = (phaseName: string) => {
    setExpandedPhases(prevState => ({
      ...prevState,
      [phaseName]: !prevState[phaseName]
    }));
  };

  const toggleDetailedSteps = () => {
    setShowDetailedSteps(prevState => !prevState);
  };

  // Check if the selected file is the workflow items configuration
  const isWorkflowItemsFile = selectedFile === '_workflow_items.yaml';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <AppBar position="static" elevation={0}>
          <Toolbar>
            ETH Innovation Platform | Scenario Flow Visualizer
          </Toolbar>
        </AppBar>
        
        <Container maxWidth="lg" className="App-main">
          <Box mt={3} mb={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
              <Box flex={{ xs: '1', md: '2' }}>
                <FileSelector 
                  onFileSelect={handleFileSelect} 
                  selectedFile={selectedFile} 
                />
              </Box>
              {!isWorkflowItemsFile && (
                <Box flex="1">
                  <ViewToggle 
                    showDetailedSteps={showDetailedSteps} 
                    onToggleDetailedSteps={toggleDetailedSteps} 
                  />
                </Box>
              )}
            </Stack>
            
            <Paper elevation={0} className="content-container">
              {loading ? (
                <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                  <CircularProgress size={40} />
                  <Typography variant="body1" mt={2}>
                    Loading workflow data...
                  </Typography>
                </Box>
              ) : error ? (
                <Box p={3}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              ) : !selectedFile ? (
                <Box p={4} textAlign="center">
                  <Typography variant="body1" color="textSecondary">
                    Select a workflow template to visualize
                  </Typography>
                </Box>
              ) : isWorkflowItemsFile ? (
                <WorkflowItems />
              ) : (
                <>
                  {workflow && (
                    <Box mb={2} className="legend-wrapper">
                      <Legend />
                    </Box>
                  )}
                  <Box p={2} className="pipeline-wrapper">
                    <PipelineView 
                      workflow={workflow} 
                      expandedPhases={expandedPhases}
                      togglePhase={togglePhase}
                      showDetailedSteps={showDetailedSteps}
                    />
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        </Container>
        
        {/* <Paper component="footer" className="App-footer" elevation={0} square>
          <Typography variant="body2" color="textSecondary" align="center">
            ETH Scenario Flow Visualization Tool
          </Typography>
        </Paper> */}
      </div>
    </ThemeProvider>
  );
}

export default App;
